import React, { useState } from "react";
import { Plus, Pencil, Search, ShieldCheck, FileCheck2, Paperclip } from "lucide-react";
import { Chip, EmptyState, Loading, ErrorState } from "../ui/primitives.jsx";
import { fmtMoney, todayStr } from "../lib/format.js";
import { useCustomers, useRentalsForCustomers, customerBalance } from "./api.js";
import CustomerForm from "./CustomerForm.jsx";

export default function CustomersTab() {
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const customers = useCustomers();
  const rentals = useRentalsForCustomers();

  const shown = (customers.data ?? []).filter((c) =>
    `${c.name} ${c.business ?? ""} ${c.phone ?? ""}`.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <>
      <div className="notice">
        <b>Card safety:</b> HALCO Ops keeps only the card type, last 4 digits, and
        expiration as a reference. Never type a full card number, CVV, or full account
        numbers here — run charges through your card reader / payment processor, which
        is built to store them securely.
      </div>

      <div className="card">
        <div className="card-h">
          <h2>Customers</h2>
          <div className="searchbox">
            <Search size={15} color="var(--muted)" />
            <input
              placeholder="Search name, business, phone"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="spacer" />
          <button className="btn primary" onClick={() => setEditing({})}>
            <Plus size={16} /> Add customer
          </button>
        </div>

        <div className="card-b tight" style={{ overflowX: "auto" }}>
          {customers.isPending ? (
            <Loading what="customers" />
          ) : customers.error ? (
            <ErrorState error={customers.error} onRetry={() => customers.refetch()} />
          ) : shown.length === 0 ? (
            <EmptyState
              title={q ? "No matches" : "No customers yet"}
              sub={
                q
                  ? "No customer matches that search."
                  : "Add a customer to attach rentals, agreements, and insurance certificates."
              }
            />
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Contact</th>
                  <th>Docs on file</th>
                  <th>Rentals</th>
                  <th>Balance</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {shown.map((c) => {
                  const { count, owed } = customerBalance(c.id, rentals.data ?? []);
                  const coiExpired = c.coi_expires && c.coi_expires < todayStr();
                  return (
                    <tr key={c.id} className="rowbtn" onClick={() => setEditing(c)}>
                      <td>
                        <div className="main-cell">{c.name}</div>
                        {c.business && <div className="sub-cell">{c.business}</div>}
                      </td>
                      <td>
                        <div>{c.phone || "—"}</div>
                        <div className="sub-cell">{c.email || ""}</div>
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {c.dl_photo_path && (
                          <span title="Driver's licence on file" style={{ marginRight: 6 }}>
                            <FileCheck2 size={16} color="var(--green)" />
                          </span>
                        )}
                        {(c.coi_path || c.coi_expires) && (
                          <span title={"COI " + (coiExpired ? "EXPIRED" : "on file")}>
                            <ShieldCheck size={16} color={coiExpired ? "var(--red)" : "var(--green)"} />
                          </span>
                        )}
                        {(c.customer_agreements?.length ?? 0) > 0 && (
                          <span title={`${c.customer_agreements.length} signed agreement(s)`} style={{ marginLeft: 6 }}>
                            <Paperclip size={16} color="var(--green)" />
                          </span>
                        )}
                        {!c.dl_photo_path && !c.coi_path && !c.coi_expires &&
                          (c.customer_agreements?.length ?? 0) === 0 && (
                            <span className="sub-cell">none</span>
                          )}
                      </td>
                      <td>{rentals.data ? count : "…"}</td>
                      <td>
                        {!rentals.data ? (
                          <span className="sub-cell">…</span>
                        ) : owed > 0 ? (
                          <Chip kind="unpaid">{fmtMoney(owed)} due</Chip>
                        ) : (
                          <Chip kind="paid">Paid up</Chip>
                        )}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <button
                          className="iconbtn"
                          title="Edit"
                          onClick={(ev) => { ev.stopPropagation(); setEditing(c); }}
                        >
                          <Pencil size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {editing !== null && <CustomerForm cust={editing} onClose={() => setEditing(null)} />}
    </>
  );
}
